import { Link } from "@tanstack/react-router";
import { PageShell, PageHero } from "@project/user/components/page-shell";
import { Button } from "@project/components/ui/button";
import { HERO_IMAGES } from "@project/lib/stock-images";
import inKindImage from "@project/assets/in-kind.png";
import scrapImage from "@project/assets/scrap.png";
import { useI18n } from "@project/lib/use-i18n";
import {
  ArrowRight,
  Heart,
  Package,
  Truck,
  Users,
  ShieldCheck,
  HandHeart,
  Recycle,
} from "lucide-react";

export default function DonationTypeSelect() {
  const { t } = useI18n();

  return (
    <PageShell>
      <PageHero
        eyebrow={t("pages.itemDonation.hero.eyebrow")}
        title={t("pages.itemDonation.hero.title")}
        subtitle={t("pages.itemDonation.hero.subtitle")}
        image={HERO_IMAGES.donate}
      />
      <section className="mx-auto max-w-5xl px-4 py-8 sm:py-12">
        <div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="anim-fade-up" style={{ animationDelay: "600ms" }}>
              <DonationTypeCard
                theme="green"
                icon={Package}
                title={t("pages.itemDonation.inKindCard.title")}
                subtitle={t("pages.itemDonation.inKindCard.subtitle")}
                description={t("pages.itemDonation.inKindCard.description")}
                features={[
                  { icon: Users, label: t("pages.itemDonation.inKindCard.feature1") },
                  { icon: ShieldCheck, label: t("pages.itemDonation.inKindCard.feature2") },
                  { icon: HandHeart, label: t("pages.itemDonation.inKindCard.feature3") },
                ]}
                image={inKindImage}
                imageAlt={t("pages.itemDonation.inKindCard.imageAlt")}
                ctaLabel={t("pages.itemDonation.inKindCard.ctaLabel")}
                ctaIcon={Heart}
                to="/donate/inkind"
              />
            </div>

            <div className="anim-fade-up" style={{ animationDelay: "740ms" }}>
              <DonationTypeCard
                theme="orange"
                icon={Truck}
                title={t("pages.itemDonation.scrapCard.title")}
                subtitle={t("pages.itemDonation.scrapCard.subtitle")}
                description={t("pages.itemDonation.scrapCard.description")}
                features={[
                  { icon: Truck, label: t("pages.itemDonation.scrapCard.feature1") },
                  { icon: Recycle, label: t("pages.itemDonation.scrapCard.feature2") },
                  { icon: HandHeart, label: t("pages.itemDonation.scrapCard.feature3") },
                ]}
                image={scrapImage}
                imageAlt={t("pages.itemDonation.scrapCard.imageAlt")}
                ctaLabel={t("pages.itemDonation.scrapCard.ctaLabel")}
                ctaIcon={Truck}
                to="/donate/scrap"
              />
            </div>
          </div>

          <p
            className="anim-fade mt-8 flex items-center justify-center gap-2 text-center text-sm text-muted-foreground"
            style={{ animationDelay: "900ms" }}
          >
            <Heart className="text-leaf h-4 w-4" />
            {t("pages.itemDonation.footerNotice")}
          </p>
        </div>
      </section>
    </PageShell>
  );
}

function DonationTypeCard({
  theme,
  icon: Icon,
  title,
  subtitle,
  description,
  features,
  image,
  imageAlt,
  ctaLabel,
  ctaIcon: CtaIcon,
  to,
}: {
  theme: "green" | "orange";
  icon: typeof Package;
  title: string;
  subtitle: string;
  description: string;
  features: { icon: typeof Users; label: string }[];
  image: string;
  imageAlt: string;
  ctaLabel: string;
  ctaIcon: typeof Heart;
  to: string;
}) {
  const isGreen = theme === "green";
  const cardBg = isGreen ? "bg-[#f0f9f4]" : "bg-[#fff7f0]";
  const borderColor = isGreen ? "border-leaf/20" : "border-gold/25";
  const iconBg = isGreen ? "bg-leaf/12 text-leaf" : "bg-gold/15 text-gold";
  const titleColor = isGreen ? "text-leaf" : "text-primary";
  const subtitleColor = isGreen ? "text-leaf/80" : "text-gold";
  const decorCircle1 = isGreen ? "bg-leaf/10" : "bg-gold/12";
  const decorCircle2 = isGreen ? "bg-leaf/8" : "bg-gold/10";
  const btnClass = isGreen
    ? "bg-leaf hover:bg-leaf/90 text-primary-foreground"
    : "hover:bg-gold/90 bg-gold text-gold-foreground";

  return (
    <div
      className={`card-lift relative h-full min-h-[365px] overflow-hidden rounded-3xl border shadow-md ${cardBg} ${borderColor}`}
    >
      <div
        className={`pointer-events-none absolute -right-4 top-10 h-36 w-36 rounded-full ${decorCircle1}`}
      />
      <div
        className={`pointer-events-none absolute bottom-20 right-0 h-44 w-44 rounded-full ${decorCircle2}`}
      />

      <div className="pointer-events-none absolute bottom-[-58px] left-[150px] z-0 h-auto w-[80%] opacity-45 [isolation:isolate] md:bottom-[-62px] md:left-[210px]">
        <img
          src={image}
          alt={imageAlt}
          className="h-full w-full object-contain [object-position:right_bottom]"
          style={{ mixBlendMode: "screen" }}
        />
      </div>

      <div className="relative z-10 flex min-h-[365px] flex-col p-6 md:p-7">
        <div className="flex items-start gap-3">
          <div
            className={`grid h-12 w-12 shrink-0 place-items-center rounded-full ${iconBg}`}
          >
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <h2
              className={`font-serif text-xl font-bold md:text-2xl ${titleColor}`}
            >
              {title}
            </h2>
            <p className={`mt-0.5 text-sm font-semibold ${subtitleColor}`}>
              {subtitle}
            </p>
          </div>
        </div>

        <p className="mt-4 w-full text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>

        <div className="mt-auto space-y-5 pt-6">
          <div className="flex flex-col gap-2.5">
            {features.map(f => (
              <div
                key={f.label}
                className="flex items-center gap-2 text-xs font-medium text-muted-foreground"
              >
                <f.icon
                  className={`h-4 w-4 shrink-0 ${isGreen ? "text-leaf" : "text-gold"}`}
                />
                {f.label}
              </div>
            ))}
          </div>

          <Button
            asChild
            size="lg"
            className={`btn-lift max-w-full gap-2 sm:max-w-[58%] ${btnClass}`}
          >
            <Link to={to}>
              <CtaIcon className="h-4 w-4" />
              {ctaLabel}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
